<template>
  <div>
    <div container>
      <div grid>
        <div column="3">
          <h4>Group</h4>
          <p>{{groupName}}</p>
        </div>
        <div column="3">
          <h4>EyeMed Member ID</h4>
          <p>{{eyemedId}}</p>
        </div>
        <div column="3">
          <h4>Client's Member ID</h4>
          <p>{{clientId}}</p>
        </div>
        <div column="3">
          <h4>Relationship</h4>
          <p>{{relationship}}</p>
        </div>
      </div>
    </div>
    <collapsible-section class='_pt0 _pb0' :collapsed='false'>
      <template #headerText>
        <p class='color-leaf'>{{coverageTitle}}</p>
      </template>
      <template #headerActions>
        <slot name="columnPicker"></slot>
      </template>
      <template #collapsibleSection>
        <form @submit.prevent="$emit('onSearch')">
          <div container>

            <list-error-warnings :warnings="warnings" />
            <div v-if="!effectiveDateEnabledByDefault && effectiveDateUnlocked" grid="row wrap">
              <div column="12">
                <div class="announcement -notice _mbxs">
                  Changing the effective date of the original coverage record may modify the members effective date, as well as other members of the family.
                </div>
              </div>
            </div>
            <div v-if="showFileBoundId" grid="row wrap">
              <div column="3">
                <app-input
                  :required='true'
                  @input="updateForm"
                  label="Filebound ID"
                  inputId="filebound-id"
                  v-model='$v.internalValue.fileBoundId.$model'
                  :errorMessage="fileBoundIdError"
                  :invalid="$v.internalValue.fileBoundId.$invalid"
                />
              </div>
            </div>
            <div grid="row wrap">
              <div column="3">
                <app-date-input
                  :required='true'
                  @input="updateForm"
                  label="Effective Date"
                  inputId="effective-date"
                  format="YYYY-MM-DD"
                  v-model='$v.internalValue.effectiveDate.$model'
                  :errorMessage="effectiveDateError"
                  :invalid="$v.internalValue.effectiveDate.$invalid"
                  :disabled="!computedEffectiveDateEnabled"/>
                <div v-if="canChangeEffectiveDate && !effectiveDateEnabledByDefault && !effectiveDateUnlocked" class="_mbxs">
                  <a @click="unlockEffectiveDate">Change Original Effective Date</a>
                </div>
              </div>
              <div column="3">
                <app-date-input
                  @input="updateForm"
                  label="Term Date"
                  inputId="term-date"
                  format="YYYY-MM-DD"
                  v-model="internalValue.termDate"/>
              </div>
            </div>
            <div grid="row wrap">
              <div column="3">
                <app-select
                  :required='true'
                  @change="subGroupUpdated"
                  label="Subgroup"
                  name="subgroup"
                  :options="subGroupOptions"
                  :invalid="$v.internalValue.subGroupCk.$invalid"
                  :errorMessage="subGroupCkError"
                  :disabled="isDependent"
                  v-model="$v.internalValue.subGroupCk.$model" />
              </div>
              <div column="3">
                <div
                  v-if="requestingNewClassPlans"
                  class="_text-center"
                  >
                  Retrieving class information
                  <async-loader
                    class="small _ptxs _pbxs"
                  />
                </div>
                <app-select
                  v-else
                  :required='true'
                  @change="classIdUpdated"
                  label="Class"
                  name="class"
                  :options="classOptions"
                  :invalid="$v.internalValue.classId.$invalid"
                  :errorMessage="classIdError"
                  :disabled="isDependent"
                  v-model="$v.internalValue.classId.$model"  />
              </div>
              <div column="3">
                <div
                  v-if="requestingNewClassPlans"
                  class="_text-center"
                  >
                  Retrieving plan information
                  <async-loader
                    class="small _ptxs _pbxs"
                  />
                </div>
                <app-select
                  v-else
                  :required='true'
                  @input="updateForm"
                  label="Plan"
                  name="plan"
                  :options="planOptions"
                  :invalid="$v.internalValue.planId.$invalid"
                  :disabled="isDependent"
                  :errorMessage="planIdError"
                  v-model="$v.internalValue.planId.$model"  />
              </div>
            </div>
          </div>

          <collapsible-section class='_pb0' :collapsed='false'>
            <template #headerText>
              <p class='color-leaf'>Reporting Codes</p>
            </template>
            <template #collapsibleSection>
              <div container>
                <div v-if="!majorDataChangeSatisfied" class="announcement _mbxs -notice _mbs">
                  <p>Before you can change reporting codes, you must modify coverage</p>
                </div>
                <div grid="row wrap">
                  <div column="3">
                    <app-input
                      v-if="codeIsFreeform(subGroupPlanInfo.benefitOptionSetting)"
                      :required='codeIsRequired(subGroupPlanInfo.benefitOptionSetting)'
                      @input="updateForm"
                      label='Benefit Option:'
                      v-model='$v.internalValue.benefitOption.$model'
                      :errorMessage="benefitOptionError"
                      :invalid="$v.internalValue.benefitOption.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                    <app-select
                      v-else
                      :required='codeIsRequired(subGroupPlanInfo.benefitOptionSetting)'
                      @input="updateForm"
                      label='Benefit Option:'
                      v-model='$v.internalValue.benefitOption.$model'
                      :options="toSelectOptions(subGroupPlanInfo.benefitOptions)"
                      :errorMessage="benefitOptionError"
                      :invalid="$v.internalValue.benefitOption.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      @hook:mounted="fixInvalidOption('benefitOption', subGroupPlanInfo.benefitOptions)"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      v-if="codeIsFreeform(subGroupPlanInfo.divisionCodeSetting)"
                      :required='codeIsRequired(subGroupPlanInfo.divisionCodeSetting)'
                      @input="updateForm"
                      label='Division Code:'
                      v-model='$v.internalValue.divisionCode.$model'
                      :errorMessage="divisionCodeError"
                      :invalid="$v.internalValue.divisionCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                    <app-select
                      v-else
                      :required='codeIsRequired(subGroupPlanInfo.divisionCodeSetting)'
                      @input="updateForm"
                      label='Division Code:'
                      v-model='$v.internalValue.divisionCode.$model'
                      :optionLabel="' '"
                      :options="toSelectOptions(subGroupPlanInfo.divisionCodes)"
                      :errorMessage="divisionCodeError"
                      :invalid="$v.internalValue.divisionCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      @hook:mounted="fixInvalidOption('divisionCode', subGroupPlanInfo.divisionCodes)"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      v-if="codeIsFreeform(subGroupPlanInfo.locationCodeSetting)"
                      :required='codeIsRequired(subGroupPlanInfo.locationCodeSetting)'
                      @input="updateForm"
                      label='Location Code:'
                      v-model='$v.internalValue.locationCode.$model'
                      :errorMessage="locationCodeError"
                      :invalid="$v.internalValue.locationCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                    <app-select
                      v-else
                      :required='codeIsRequired(subGroupPlanInfo.locationCodeSetting)'
                      @input="updateForm"
                      label='Location Code:'
                      v-model='$v.internalValue.locationCode.$model'
                      :options="toSelectOptions(subGroupPlanInfo.locationCodes)"
                      :errorMessage="locationCodeError"
                      :invalid="$v.internalValue.locationCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      @hook:mounted="fixInvalidOption('locationCode', subGroupPlanInfo.locationCodes)"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      v-if="codeIsFreeform(subGroupPlanInfo.companyCodeSetting)"
                      :required='codeIsRequired(subGroupPlanInfo.companyCodeSetting)'
                      @input="updateForm"
                      label='Company Code:'
                      v-model='$v.internalValue.companyCode.$model'
                      :errorMessage="companyCodeError"
                      :invalid="$v.internalValue.companyCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                    <app-select
                      v-else
                      :required='codeIsRequired(subGroupPlanInfo.companyCodeSetting)'
                      @input="updateForm"
                      label='Company Code:'
                      v-model='$v.internalValue.companyCode.$model'
                      :options="toSelectOptions(subGroupPlanInfo.companyCodes)"
                      :errorMessage="companyCodeError"
                      :invalid="$v.internalValue.companyCode.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      @hook:mounted="fixInvalidOption('companyCode', subGroupPlanInfo.companyCodes)"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Client Group #"
                      valueKey="client-group-number"
                      v-model='$v.internalValue.clientGroupNumber.$model'
                      :errorMessage="clientGroupNumberError"
                      :invalid="$v.internalValue.clientGroupNumber.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Reporting Code 1"
                      valueKey="reporting-code-1"
                      v-model='$v.internalValue.reportingCode1.$model'
                      :errorMessage="reportingCode1Error"
                      :invalid="$v.internalValue.reportingCode1.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Reporting Code 2"
                      valueKey="reporting-code-2"
                      v-model='$v.internalValue.reportingCode2.$model'
                      :errorMessage="reportingCode2Error"
                      :invalid="$v.internalValue.reportingCode2.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Reporting Code 3"
                      valueKey="reporting-code-3"
                      v-model='$v.internalValue.reportingCode3.$model'
                      :errorMessage="reportingCode3Error"
                      :invalid="$v.internalValue.reportingCode3.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Reporting Code 4"
                      valueKey="reporting-code-4"
                      v-model='$v.internalValue.reportingCode4.$model'
                      :errorMessage="reportingCode4Error"
                      :invalid="$v.internalValue.reportingCode4.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                  <div column="3">
                    <app-input
                      @input="updateForm"
                      label="Reporting Code 5"
                      valueKey="reporting-code-5"
                      v-model='$v.internalValue.reportingCode5.$model'
                      :errorMessage="reportingCode5Error"
                      :invalid="$v.internalValue.reportingCode5.$invalid"
                      :disabled="!majorDataChangeSatisfied"
                      />
                  </div>
                </div>
              </div>
            </template>
          </collapsible-section>
        </form>
      </template>
    </collapsible-section>
    <collapsible-section v-if="showRelatedMembersTable && relatedMembers.length > 0" class='_pt0 _pb0' :collapsed='false'>
      <template #headerText>
        <p class='color-leaf'>{{ relatedMembersTableReadOnly ? 'Dependents' : 'Apply To Dependents' }}</p>
      </template>
      <template #collapsibleSection>

        <div container>
          <div v-if="dependentTableNotice" class="announcements _mbxs">
            <div class="announcement -notice">
              {{ dependentTableNotice }}
            </div>
          </div>
          <app-advanced-table
            class="_m0 _p0"
            title="Group Results"
            :headers="relatedMembersHeaders"
            :items="relatedMembers"
            :rowsPerPage="10"
          >
            <template
              #items="{ item, visibleHeaders, getCellValue }"

            >
              <tr>
                <template v-for="(header, index) in visibleHeaders">
                  <td v-if="header.value === 'checkbox'">
                    <app-checkbox
                      v-if="dependentAvailableForSelection(item)"
                      @click="toggleMember($event, item)"
                      :valueKey='`member-${item.memberCk}`'
                      label='' />
                  </td>
                  <td v-else>{{ getCellValue(item, header) }}</td>
                </template>
              </tr>
            </template>
          </app-advanced-table>
        </div>
      </template>
    </collapsible-section>
  </div>
</template>

<script>
  import DateHelper from '@mixins/DateHelper'
  import CollapsibleSection from '@sections/CollapsibleSection'
  import AppSelect from '@atoms/Select'
  import AppInput from '@atoms/Input'
  import AppCheckbox from '@atoms/Checkbox'
  import AppDateInput from '@atoms/DateInput'
  import AsyncLoader from '@molecules/AsyncLoader'
  import AppAdvancedTable from '@organisms/AdvancedTable.vue'

  import formReady from '@mixins/formReady'
  import validationMessages from '@mixins/validationMessagesInternalValue'
  import ReportingCodes from '@mixins/ReportingCodes'
  import formattable from '@mixins/formattable'
  import ListErrorWarnings from '@mixins/ListErrorWarnings'

  import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
  import { falsyAwareStringCompare, fileBoundIdValidation, formatDate } from '@/utils'

  export default {
    mixins: [
      DateHelper,
      validationMessages,
      formReady,
      ReportingCodes,
      formattable,
      ListErrorWarnings
    ],
    components: {
      AppAdvancedTable,
      CollapsibleSection,
      AppSelect,
      AsyncLoader,
      AppDateInput,
      AppInput,
      AppCheckbox
    },
    props: {
      value: Object,
      coverageTitle: String,
      planOptions: Array,
      classOptions: Array,
      subGroupOptions: Array,
      memberName: String,
      groupName: String,
      eyemedId: String,
      clientId: String,
      relationship: String,
      effectiveDateEnabledByDefault: {
        type: Boolean,
        default: true
      },
      warnings: Array,
      relatedMembers: Array,
      showRelatedMembersTable: Boolean,
      relatedMembersTableReadOnly: {
        type: Boolean,
        default: false
      },
      showFileBoundId: {
        type: Boolean,
        default: false
      },
      subGroupPlanInfo: {
        type: Object,
        default() {
          return {
            locationCodeSetting: null,
            companyCodeSetting: null,
            divisionCodeSetting: null,
            benefitOptionSetting: null,
            locationCodes: [],
            companyCodes: [],
            divisionCodes: [],
            benefitOptions: []
          }
        }
      },
      dependentAvailableForSelection: {
        type: Function,
        default() { return true }
      },
      initialCoverage: {
        type: Object,
        default() {
          return {
            effectiveDate: '',
            termDate: '',
            subGroupId: '',
            subGroupCk: '',
            classId: '',
            planId: '',
            previousEffectiveDate: '',
            previousTerminationDate: '',
            previousSubGroupCk: '',
            previousClassId: '',
            previousPlanId: '',
            benefitOption: '',
            divisionCode: '',
            companyCode: '',
            locationCode: '',
            clientGroupNumber: '',
            reportingCode1: '',
            reportingCode2: '',
            reportingCode3: '',
            reportingCode4: '',
            reportingCode5: '',
            dependents: '',
            subGroupChg: ''
          }
        }
      },
      isDependent: {
        type: Boolean,
        default: false
      },
      primarySubscriberCoverages: Array,
      canChangeEffectiveDate: {
        type: Boolean,
        default: true
      },
      dependentTableNotice: String,
      requestingNewClassPlans: Boolean
    },
    computed: {
      computedEffectiveDateEnabled() {
        return this.effectiveDateEnabledByDefault || this.effectiveDateUnlocked
      },
      majorDataChangeSatisfied() {
        if (this.$route.name === 'coverage-add') {
          return true
        }

        const majorDataChangeSatisfied = !falsyAwareStringCompare(formatDate(this.initialCoverage.effectiveDate), formatDate(this.internalValue.effectiveDate)) ||
          !falsyAwareStringCompare(formatDate(this.initialCoverage.termDate), formatDate(this.internalValue.termDate)) ||
          !falsyAwareStringCompare(this.initialCoverage.classId, this.internalValue.classId) ||
          !falsyAwareStringCompare(this.initialCoverage.planId, this.internalValue.planId)

        this.$emit('majorDataChangeSatisfied', majorDataChangeSatisfied)
        return majorDataChangeSatisfied
      },
      ready () {
        return !this.$v.$invalid && this.majorDataChangeSatisfied
      }
    },
    validations: {
      internalValue: {
        fileBoundId: {
          required: requiredIf(function() {
            return this.showFileBoundId
          }),
          maxLength: maxLength(15),
          fileBoundIdValidation
        },
        effectiveDate: {
          required
        },
        subGroupCk: {
          required
        },
        classId: {
          required
        },
        planId: {
          required
        },
        locationCode: {
          required: requiredIf(function() { return this.codeIsRequired(this.subGroupPlanInfo.locationCodeSetting) }),
          maxLength: maxLength(20)
        },
        companyCode: {
          required: requiredIf(function() { return this.codeIsRequired(this.subGroupPlanInfo.companyCodeSetting) }),
          maxLength: maxLength(20)
        },
        divisionCode: {
          required: requiredIf(function() { return this.codeIsRequired(this.subGroupPlanInfo.divisionCodeSetting) }),
          maxLength: maxLength(20)
        },
        clientGroupNumber: {
          maxLength: maxLength(20)
        },
        benefitOption: {
          required: requiredIf(function() { return this.codeIsRequired(this.subGroupPlanInfo.benefitOptionSetting) }),
          maxLength: maxLength(4)
        },
        reportingCode1: {
          maxLength: maxLength(20)
        },
        reportingCode2: {
          maxLength: maxLength(20)
        },
        reportingCode3: {
          maxLength: maxLength(20)
        },
        reportingCode4: {
          maxLength: maxLength(20)
        },
        reportingCode5: {
          maxLength: maxLength(20)
        }
      }
    },
    watch: {
      value(newValue) {
        Object.keys(this.internalValue).forEach((key) => {
          if (newValue[key]) {
            this.$set(this.internalValue, key, newValue[key])
          }
        })

        if (!this.internalValue.members) {
          this.internalValue.members = []
        }
      },
      'internalValue.subGroupCk': function(newVal) {
        this.internalValue.subGroupChg = true
      },
      'classOptions'(classOptions) {
        if (classOptions.length !== 1) {
          return
        }

        const newClassId = this.classOptions[0].value
        if (this.internalValue.classId !== newClassId) {
          this.internalValue.classId = newClassId
        }
      },
      'planOptions'(planOptions) {
        if (planOptions.length !== 1) {
          return
        }

        const newPlanId = this.planOptions[0].value
        if (this.internalValue.planId !== newPlanId) {
          this.internalValue.planId = newPlanId
        }
      },
      ready (val) {
        this.$emit('ready', val)
      }
    },
    data() {
      return {
        submitting: false,
        effectiveDateUnlocked: this.effectiveDateEnabledByDefault,
        internalValue: {
          fileBoundId: this.value.fileBoundId,
          effectiveDate: this.value.effectiveDate,
          termDate: this.value.termDate,
          groupCk: this.value.groupCk,
          subGroupCk: this.value.subGroupCk,
          classId: this.value.classId,
          planId: this.value.planId,
          previousEffectiveDate: this.value.effectiveDate,
          previousTerminationDate: this.value.termDate,
          previousSubGroupCk: this.value.subGroupCk,
          previousClassId: this.value.classId,
          previousPlanId: this.value.planId,
          benefitOption: this.value.benefitOption,
          locationCode: this.value.locationCode,
          divisionCode: this.value.divisionCode,
          companyCode: this.value.companyCode,
          clientGroupNumber: this.value.clientGroupNumber,
          reportingCode1: this.value.reportingCode1,
          reportingCode2: this.value.reportingCode2,
          reportingCode3: this.value.reportingCode3,
          reportingCode4: this.value.reportingCode4,
          reportingCode5: this.value.reportingCode5,
          relatedMembers: this.value.relatedMembers,
          subGroupChg: false,
          members: []
        },
        relatedMembersHeaders: [
          { text: '', value: 'checkbox', sortable: false, visible: !this.relatedMembersTableReadOnly },
          { text: 'Name', value: (item) => `${item.firstName} ${item.lastName}` },
          { text: 'Client\'s Member ID', value: 'clientHealthId' },
          { text: 'DOB', value: (item) => this.formatDate(item.dateOfBirth) },
          { text: 'Relationship', value: 'relationshipDescription' },
          { text: 'Effective Date', value: (item) => item.coverageStartDate ? this.formatDate(item.coverageStartDate) : '' },
          { text: 'Term Date', value: (item) => item.coverageEndDate ? this.formatDate(item.coverageEndDate) : '' },
          { text: 'Status', value: (item) => item.memberStatus === 'Y' ? 'Active' : 'Inactive' }
        ]
      }
    },
    methods: {
      // sometimes, when switching from a text input to a select dropdown, the model value that
      // was entered as text is not available as an option. set the model to emtpy if so
      fixInvalidOption(model, options) {
        const currentVal = this.internalValue[model]
        if (!currentVal) {
          return
        }

        const modelOption = options.find(option => option === currentVal)
        if (modelOption) {
          return
        }

        this.internalValue[model] = ''
      },
      unlockEffectiveDate() {
        this.effectiveDateUnlocked = true
        this.$emit('unlockEffectiveDate', this.effectiveDateUnlocked)
      },
      toggleMember(checked, member) {
        if (checked) {
          const memberObject = {
            memberCk: member.memberCk,
            memberType: member.memberType,
            actionType: 'AddCoverage',
            memberVoided: member.memberVoided
          }

          this.internalValue.members.push(memberObject)
        } else {
          const index = this.internalValue.members.findIndex((m) => m.memberCk === member.memberCk)
          this.$delete(this.internalValue.members, index)
        }

        this.$emit('input', this.internalValue)
      },
      subGroupUpdated() {
        this.internalValue.classId = ''
        this.internalValue.planId = ''
        this.updateForm()
      },
      classIdUpdated() {
        this.internalValue.planId = ''
        this.updateForm()
      },
      updateForm() {
        this.$emit('input', this.internalValue)
      }
    },
    mounted() {
      this.$emit('ready', this.ready)
    }
  }
</script>
